import { getEntityTypeName } from 'globalFunctions';
import { TFunction } from 'i18next';
import { EntityTypes } from 'models/entity';
import { CopilotScene } from '../copilotScene';
import { AIActionKind, AIPromptActionTemplate } from './aiActionTemplate';

/**
 * The ready-made questions every entity scene shares. The entity travels in the request (the entity ref of
 * a detail scene, the entity type of a list scene); the prompt only names its type, in the user's language,
 * so it reads as a normal chat message. Each template is bound once per surface, so none anchors.
 */
export type AIEntityActionParams = {
  entityType: EntityTypes;
};

export type AISceneActionParams = {
  scene: CopilotScene;
};

// Control and Requirement: what an auditor checks for this one
export const entityExplainAuditTemplate: AIPromptActionTemplate<AIEntityActionParams> = {
  id: 'entity.explainAudit',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Entity.ExplainAudit.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return t('copilot:Actions.Entity.ExplainAudit.Prompt', { entity: getEntityTypeName(params.entityType, t) });
  },
};

// Control and Requirement: the gaps that keep this one from being audit ready, ranked (the suggest-improvement skill)
export const entitySuggestImprovementTemplate: AIPromptActionTemplate<AIEntityActionParams> = {
  id: 'entity.suggestImprovement',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Entity.SuggestImprovement.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return t('copilot:Actions.Entity.SuggestImprovement.Prompt', { entity: getEntityTypeName(params.entityType, t) });
  },
};

// Switches over literal keys: plurals differ per language, and the translation scanner cannot follow a
// key built from the scene name.
const getSceneEntities = (t: TFunction<string[]>, scene: CopilotScene): string => {
  switch (scene) {
    case CopilotScene.Risks:
      return t('copilot:Actions.Scenes.Risks.Entities');
    case CopilotScene.Processes:
      return t('copilot:Actions.Scenes.Processes.Entities');
    case CopilotScene.Objectives:
      return t('copilot:Actions.Scenes.Objectives.Entities');
    case CopilotScene.MyTasks:
      return t('copilot:Actions.Scenes.MyTasks.Entities');
    case CopilotScene.MyRequests:
      return t('copilot:Actions.Scenes.MyRequests.Entities');
    case CopilotScene.AllTasks:
      return t('copilot:Actions.Scenes.AllTasks.Entities');
    case CopilotScene.Assets:
      return t('copilot:Actions.Scenes.Assets.Entities');
    case CopilotScene.Requirements:
      return t('copilot:Actions.Scenes.Requirements.Entities');
    case CopilotScene.Controls:
      return t('copilot:Actions.Scenes.Controls.Entities');
    case CopilotScene.ProcessingActivities:
      return t('copilot:Actions.Scenes.ProcessingActivities.Entities');
    default:
      return '';
  }
};

// The selection turn's wording on a list scene, undefined for a scene without a summarize action
export const getSceneSelectionQuestion = (
  t: TFunction<string[]>,
  scene: CopilotScene,
  count: number,
): string | undefined => {
  switch (scene) {
    case CopilotScene.Risks:
      return t('copilot:Actions.Scenes.Risks.SummarizeSelected', { count });
    case CopilotScene.Processes:
      return t('copilot:Actions.Scenes.Processes.SummarizeSelected', { count });
    case CopilotScene.Objectives:
      return t('copilot:Actions.Scenes.Objectives.SummarizeSelected', { count });
    case CopilotScene.MyTasks:
      return t('copilot:Actions.Scenes.MyTasks.SummarizeSelected', { count });
    case CopilotScene.MyRequests:
      return t('copilot:Actions.Scenes.MyRequests.SummarizeSelected', { count });
    case CopilotScene.AllTasks:
      return t('copilot:Actions.Scenes.AllTasks.SummarizeSelected', { count });
    case CopilotScene.Assets:
      return t('copilot:Actions.Scenes.Assets.SummarizeSelected', { count });
    case CopilotScene.Requirements:
      return t('copilot:Actions.Scenes.Requirements.SummarizeSelected', { count });
    case CopilotScene.Controls:
      return t('copilot:Actions.Scenes.Controls.SummarizeSelected', { count });
    case CopilotScene.ProcessingActivities:
      return t('copilot:Actions.Scenes.ProcessingActivities.SummarizeSelected', { count });
    default:
      return undefined;
  }
};

// Bound only while the list has rows selected; the menu names the selection (getSceneSelectionQuestion), the
// prompt words the turn and the ids reach the agent as the selection (the summarize-selection skill).
export const entitySummarizeSelectionTemplate: AIPromptActionTemplate<AISceneActionParams> = {
  id: 'entity.summarizeSelection',
  kind: AIActionKind.Prompt,
  acceptsSelection: true,
  requiresSelection: true,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Entity.Summarize.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return t('copilot:Actions.Entity.Summarize.Prompt', { entities: getSceneEntities(t, params.scene) });
  },
};
